import { useMemo, useState } from 'react';
import { sortHand, suggestJudgementBid, type JudgementLegal, type JudgementView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { seatPosition, SeatView, MySeat, useNarrow, useSpeech } from './common';

const OFFSETS: Record<string, { x: number; y: number }> = {
  bottom: { x: 0, y: 34 },
  left: { x: -62, y: 0 },
  top: { x: 0, y: -34 },
  right: { x: 62, y: 0 },
};
const SUIT_SYMBOL: Record<string, string> = { S: '♠', H: '♥', D: '♦', C: '♣' };

export function JudgementTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as JudgementView;
  const legal = useGame((s) => s.legal) as JudgementLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const speech = useSpeech();
  const [selected, setSelected] = useState<string | null>(null);
  const hand = useMemo(() => sortHand(view.hand), [view.hand]);
  const playable = new Set(legal?.kind === 'play' ? legal.cards : []);
  const bidLegal = legal?.kind === 'bid' ? legal : null;
  const suggested = useMemo(
    () => (bidLegal ? suggestJudgementBid(view, bidLegal) : 0),
    // The suggestion only depends on my hand, the trump card and the dealer's restriction.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [view.hand.join(), view.turnCard, bidLegal?.forbidden, bidLegal?.max],
  );
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const narrow = useNarrow();
  const trickSize = narrow ? 'md' : 'lg';
  const k = narrow ? 0.7 : 1;
  const tricksTag = (s: number) => (view.bids[s] === null ? null : t('cb.tricks', { won: view.tricksWon[s] ?? 0, bid: view.bids[s] ?? '-' }));
  const trumpRed = view.trump === 'H' || view.trump === 'D';

  const onCard = (c: string) => {
    if (!myTurn || legal?.kind !== 'play' || !playable.has(c)) return;
    setSelected(c);
    void act({ type: 'play', card: c }).finally(() => setSelected(null));
  };

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1, 2, 3].map((s) => (
          <SeatView
            key={s}
            seat={s}
            info={seats[s]}
            mySeat={mySeat}
            total={4}
            turn={turn}
            speech={speech[s]}
            sub={<span className="dim num">{view.totals[s] ?? 0} pts</span>}
            tags={
              <>
                {view.dealer === s && <span className="tag">D</span>}
                {tricksTag(s) && <span className="tag">{tricksTag(s)}</span>}
              </>
            }
          />
        ))}
        <div className="felt-center centered">
          <div className="center-note">
            {t('ju.round', { n: view.round, total: view.totalRounds })} · {view.cards === 1 ? t('ju.card') : t('ju.cards', { n: view.cards })}
          </div>
          <div className="center-note ju-trump" role="status">
            {t('ju.trump')}: <b style={{ color: trumpRed ? 'var(--danger)' : undefined }}>{SUIT_SYMBOL[view.trump]}</b>
            {view.phase === 'BIDDING' && !myTurn && turn?.seats[0] !== undefined && ` · ${t('table.waitingFor', { name: name(turn.seats[0]) })}`}
          </div>
          <div className={`trick ${view.phase === 'TRICK_END' ? 'collect' : ''}`} key={`trick-${view.round}-${view.tricks.length}-${view.phase === 'TRICK_END'}`} aria-label="Cards on the table">
            {view.trick.map((p, i) => {
              const dir = seatPosition(p.seat, mySeat, 4).dir;
              const o = OFFSETS[dir]!;
              const off = { x: o.x * k, y: o.y * k };
              const w = view.phase === 'TRICK_END' && view.lastTrick ? OFFSETS[seatPosition(view.lastTrick.winner, mySeat, 4).dir]! : null;
              const winnerDir = w ? { x: w.x * k, y: w.y * k } : null;
              return (
                <div
                  key={p.card}
                  className="slot fly"
                  style={{
                    transform: `translate(calc(-50% + ${off.x}px), calc(-50% + ${off.y}px)) rotate(${(i - 1.5) * 4}deg)`,
                    ['--from-x' as string]: `${off.x * 3}px`,
                    ['--from-y' as string]: `${off.y * 4}px`,
                    ...(winnerDir ? { ['--to-x' as string]: `${winnerDir.x * 3}px`, ['--to-y' as string]: `${winnerDir.y * 4}px`, animationDelay: '0.45s' } : {}),
                    zIndex: i,
                  }}
                >
                  <PlayingCard id={p.card} size={trickSize} />
                </div>
              );
            })}
          </div>
          {view.phase === 'ROUND_END' && view.roundResults.length > 0 && (
            <div className="card glass" style={{ textAlign: 'center' }}>
              <strong>{t('sp.roundScores', { n: view.round })}</strong>
              <div className="row" style={{ justifyContent: 'center', marginTop: 6 }}>
                {view.roundResults.at(-1)!.scores.map((sc, s) => (
                  <span key={s} className={`pill ${sc > 0 ? 'accent' : 'danger'}`}>
                    {name(s)}: {sc > 0 ? '+' : ''}{sc}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={mySeat !== null ? <>{view.totals[mySeat] ?? 0} pts{tricksTag(mySeat) ? ` · ${tricksTag(mySeat)}` : ''}</> : null} />
        <div className="ju-turn-card" aria-label={t('ju.trumpCard')}>
          <PlayingCard id={view.turnCard} size="sm" />
        </div>
      </div>
      <div className="table-bottom">
        {myTurn && bidLegal && <BidPicker min={bidLegal.min} max={bidLegal.max} forbidden={bidLegal.forbidden} suggested={suggested} onBid={(v) => void act({ type: 'bid', value: v })} />}
        <Hand size="lg" label="Your hand">
          {hand.map((c, i) => (
            <PlayingCard
              key={c}
              id={c}
              size="lg"
              dealIndex={view.phase === 'BIDDING' ? i : undefined}
              selected={selected === c}
              playable={myTurn && playable.has(c)}
              unplayable={myTurn && legal?.kind === 'play' && !playable.has(c)}
              onClick={() => onCard(c)}
            />
          ))}
        </Hand>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              {[0, 1, 2, 3].map((s) => (
                <th key={s} className="ellipsis" style={{ maxWidth: 70 }}>{name(s)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.roundResults.map((row, r) => (
              <tr key={r}>
                <td>
                  R{r + 1}
                  <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{row.cards}{SUIT_SYMBOL[row.trump]}</div>
                </td>
                {row.scores.map((v, s) => (
                  <td key={s} className="num" style={{ color: v < 0 ? 'var(--danger)' : undefined }}>
                    {v > 0 ? '+' : ''}{v}
                    <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{row.won[s]}/{row.bids[s]}</div>
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td><strong>Total</strong></td>
              {view.totals.map((v, s) => (
                <td key={s} className="num"><strong>{v}</strong></td>
              ))}
            </tr>
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('ju.scoreHint')}</p>
      </Modal>
    </>
  );
}

/** Bid picker: 0..cards, the suggested bid starred, the dealer's barred bid disabled, and a separate confirm button. */
function BidPicker({ min, max, forbidden, suggested, onBid }: { min: number; max: number; forbidden: number | null; suggested: number; onBid: (v: number) => void }) {
  const t = useT();
  const [value, setValue] = useState(suggested);
  const [sent, setSent] = useState(false);
  const values = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  const chosen = value === forbidden ? suggested : value;
  return (
    <div className="bid-picker" role="group" aria-label={t('ju.bid')}>
      <div className="bid-head">
        <strong>{t('ju.yourBid')}</strong>
        <span className="dim">
          ★ {t('cb.suggested')}: <b>{suggested}</b>
        </span>
      </div>
      <div className="bid-chips" role="radiogroup" aria-label={t('ju.bid')}>
        {values.map((v) => (
          <button
            key={v}
            role="radio"
            aria-checked={chosen === v}
            disabled={v === forbidden}
            className={`bid-chip ${chosen === v ? 'on' : ''} ${v === suggested ? 'star' : ''}`}
            onClick={() => setValue(v)}
            aria-label={`${v}${v === suggested ? ` (${t('cb.suggested')})` : ''}`}
          >
            {v}
          </button>
        ))}
      </div>
      {forbidden !== null && <div className="dim" style={{ fontSize: 'var(--fs-xs)', textAlign: 'center' }}>{t('ju.hook', { n: forbidden })}</div>}
      <button
        className="btn primary block bid-confirm"
        disabled={sent}
        onClick={() => {
          setSent(true);
          onBid(chosen);
          setTimeout(() => setSent(false), 1500);
        }}
      >
        {t('ju.bidValue', { n: chosen })}
      </button>
    </div>
  );
}
